import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { formatUtc } from '@/lib/format-utc'
import type { UnmappedFieldDTO } from '@/services/workfront-mappings.service'
import { ListState, type ListStateProps } from './ListState'

export interface UnmappedFieldActions {
	onMap: (field: UnmappedFieldDTO) => void
	onIgnore: (field: UnmappedFieldDTO) => void
}

/** The field's name as a person reads it: its label, else its `DE:` name, else its ID. */
export const unmappedFieldName = (field: UnmappedFieldDTO) => field.label ?? field.name ?? field.parameterId

/** One Workfront field no mapping reads: where it is, how often it is filled, with Map and Ignore. */
export function UnmappedFieldRow({ field, onMap, onIgnore }: UnmappedFieldActions & { field: UnmappedFieldDTO }) {
	const name = unmappedFieldName(field)
	return (
		<TableRow>
			<TableCell>
				<span className="block font-medium">{name}</span>
				<span className="text-ds-xs text-ds-text-muted">{field.parameterId}</span>
			</TableCell>
			<TableCell>
				<span className="block">{field.objCodes.join(', ')}</span>
				<span className="text-ds-xs text-ds-text-muted">{field.forms.join(', ') || 'on no form'}</span>
			</TableCell>
			<TableCell>{field.objectCount}</TableCell>
			<TableCell className="text-ds-sm">{field.sampleValues.slice(0, 3).join(' · ') || '—'}</TableCell>
			<TableCell>{field.firstSeenAt ? formatUtc(field.firstSeenAt) : '—'}</TableCell>
			<TableCell>{field.lastSeenAt ? formatUtc(field.lastSeenAt) : '—'}</TableCell>
			<TableCell>
				<span className="flex gap-2">
					<Button size="sm" variant="outline" aria-label={`Map ${name}`} onPress={() => onMap(field)}>
						Map this field
					</Button>
					<Button size="sm" variant="ghost" aria-label={`Ignore ${name}`} onPress={() => onIgnore(field)}>
						Ignore
					</Button>
				</span>
			</TableCell>
		</TableRow>
	)
}

export interface UnmappedFieldsTableProps
	extends UnmappedFieldActions,
		Pick<ListStateProps, 'isLoading' | 'isError' | 'onRetry'> {
	fields: UnmappedFieldDTO[]
	filtered?: boolean
}

/** Every Workfront field carrying values on stored objects that no enabled mapping reads. */
export function UnmappedFieldsTable({ fields, filtered, onMap, onIgnore, ...state }: UnmappedFieldsTableProps) {
	return (
		<ListState
			noun="unmapped fields"
			{...state}
			isEmpty={fields.length === 0}
			emptyTitle={filtered ? 'No unmapped fields match' : 'Every Workfront field in use is mapped'}
		>
			<Table aria-label="Unmapped fields">
				<TableHeader>
					<TableRow>
						{[
							'Workfront field',
							'Objects and forms',
							'With a value',
							'Sample values',
							'First seen',
							'Last seen',
							'',
						].map((column) => (
							<TableHead key={column || 'actions'}>{column}</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{fields.map((field) => (
						<UnmappedFieldRow key={field.parameterId} field={field} onMap={onMap} onIgnore={onIgnore} />
					))}
				</TableBody>
			</Table>
		</ListState>
	)
}
